<template>
  <div class="log-form">
    <div class="lf-head">
      <strong>{{ t('actions.form.title') }}</strong>
      <span class="lf-note">{{ t('actions.form.note') }}</span>
    </div>

    <label class="lf-fld">
      <span>{{ t('actions.form.what') }}</span>
      <textarea v-model="what" rows="2" :placeholder="t('actions.form.whatPlaceholder')"></textarea>
    </label>

    <div class="lf-row">
      <label class="lf-fld">
        <span>{{ t('actions.form.where') }}</span>
        <select v-model="place">
          <option :value="WHOLE_STORE">{{ t('actions.wholeStore') }}</option>
          <optgroup v-for="g in groups" :key="g.label" :label="groupLabel(g.label)">
            <option v-for="n in g.names" :key="n" :value="n">{{ n }}</option>
          </optgroup>
        </select>
      </label>
      <label class="lf-fld">
        <span>{{ t('actions.form.when') }}</span>
        <input type="date" v-model="pivot" :max="todayIso" />
      </label>
    </div>

    <!-- Only what THIS place can measure: built from the zone's own configuration. -->
    <fieldset class="lf-fld">
      <legend>{{ t('actions.form.goal') }}</legend>
      <p class="lf-hint">{{ t('actions.form.goalHint') }}</p>
      <div class="lf-options">
        <label v-for="m in options" :key="m" class="lf-opt" :class="{ on: goal === m }">
          <input type="radio" :value="m" v-model="goal" />
          <span class="lf-opt-name">{{ label(m) }}</span>
          <span v-if="HINT[m]" class="lf-opt-hint">{{ hint(m) }}</span>
        </label>
      </div>
    </fieldset>

    <fieldset v-if="alsoOptions.length" class="lf-fld">
      <legend>{{ t('actions.form.also') }} <span class="lf-optional">{{ t('actions.form.optional') }}</span></legend>
      <div class="lf-checks">
        <label v-for="m in alsoOptions" :key="m" class="lf-check">
          <input type="checkbox" :value="m" v-model="also" /> {{ label(m) }}
        </label>
      </div>
    </fieldset>

    <div class="lf-actions">
      <Tooltip v-if="locked" :text="t('actions.form.lockedTip')" placement="top" align="start">
        <button type="button" class="lf-submit is-locked" aria-disabled="true">
          <i class="bi bi-lock-fill"></i> {{ t('actions.form.track') }}
        </button>
      </Tooltip>
      <button v-else type="button" class="lf-submit" :disabled="!canSubmit || busy" @click="submit">
        {{ t('actions.form.track') }}
      </button>
      <button type="button" class="lf-cancel" @click="$emit('cancel')">{{ t('actions.cancel') }}</button>
    </div>
  </div>
</template>

<script setup>
// The "Log your own change" form, shared by the live Actions tab and the demo.
// Places and metrics come from watchable.js — what each zone can actually measure.
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Tooltip from '@/components/ui/Tooltip.vue'
import { WHOLE_STORE, watchableFor, placeGroups, zoneByName, isEntranceLike } from '@/guidance/watchable'

const props = defineProps({
  zones: { type: Array, default: () => [] },
  todayIso: { type: String, required: true },
  locked: { type: Boolean, default: false },   // demo: show, don't save
  busy: { type: Boolean, default: false },
})
const emit = defineEmits(['submit', 'cancel'])
const { t } = useI18n()

const NAME = {
  store_traffic: 'Store traffic (all doors)',
  reach: 'Reach',
  engagement: 'Engagement',
  conversion: 'Conversion',
  purchase_rate: 'Purchase rate',
  dwell: 'Time in zone',
  dwell_est: 'Time in zone (estimated)',
  transit: 'Transit time (estimated)',
  wait: 'Wait time',
  interaction: 'Service time',
  service_response: 'Customers not helped',
}
const HINT = {
  store_traffic: 'everyone entering the store',
  reach: 'share of store visitors who come here',
  engagement: 'share of visitors who stop',
  conversion: 'stoppers who buy — needs sales figures',
  purchase_rate: 'visitors who buy — needs sales figures',
  dwell_est: 'from in + out on the tripwire',
  transit: 'time to get here from the source line',
  service_response: 'shoppers worth approaching that nobody came to',
}

const what = ref('')
const place = ref(WHOLE_STORE)
const pivot = ref(props.todayIso)
const goal = ref(null)
const also = ref([])

const groups = computed(() => placeGroups(props.zones))
const zone = computed(() => zoneByName(props.zones, place.value))
const options = computed(() => watchableFor(zone.value))
const alsoOptions = computed(() => options.value.filter((m) => m !== goal.value))

// NAME / HINT are the English; the reader sees actions.form.metric.* / metricHint.*.
const label = (m) => {
  if (m === 'traffic') {
    if (!zone.value) return t('actions.form.metric.storeTrafficPlain')
    return isEntranceLike(zone.value) ? t('actions.form.metric.doorTraffic') : t('actions.form.metric.zoneTraffic')
  }
  return NAME[m] ? t(`actions.form.metric.${m}`) : m
}
const hint = (m) => (HINT[m] ? t(`actions.form.metricHint.${m}`) : '')
// Place groups arrive as their English names (watchable.js); shown translated.
const GROUP_KEY = {
  Store: 'store', Checkouts: 'checkouts', 'Service points': 'servicePoints',
  'Product, demo & display zones': 'productZones',
}
const groupLabel = (label) => (GROUP_KEY[label] ? t(`actions.form.groups.${GROUP_KEY[label]}`) : label)

// New place → default goal = its first metric; drop "also" picks it can't measure.
watch(options, (opts) => {
  goal.value = opts[0] || null
  also.value = also.value.filter((m) => opts.includes(m) && m !== goal.value)
}, { immediate: true })
watch(goal, (g) => { also.value = also.value.filter((m) => m !== g) })

const canSubmit = computed(() => !!what.value.trim() && !!goal.value)
function submit() {
  if (!canSubmit.value) return
  emit('submit', {
    what: what.value.trim(),
    zone: zone.value ? zone.value.DetectionZoneName : null,
    pivot: pivot.value || props.todayIso,
    metrics: [goal.value, ...also.value],   // the goal FIRST — that's what judges it
  })
}
</script>

<style scoped>
.log-form {
  margin-bottom: 16px;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #e6edf3;
  border-radius: 14px;
  max-width: 720px;
}
.lf-head { display: flex; flex-direction: column; gap: 2px; margin-bottom: 14px; }
.lf-head strong { color: #1d2b3a; font-size: 1rem; }
.lf-note { color: #6b7a87; font-size: 0.82rem; }
.lf-fld { display: flex; flex-direction: column; gap: 6px; margin: 0 0 14px; padding: 0; border: none; }
.lf-fld > span, .lf-fld legend { color: #425461; font-size: 0.82rem; font-weight: 700; padding: 0; }
.lf-fld textarea, .lf-fld select, .lf-fld input[type='date'] {
  padding: 7px 9px;
  border: 1px solid #d3dde7;
  border-radius: 8px;
  color: #1d2b3a;
  font: inherit;
  font-size: 0.88rem;
  background: #fff;
}
.lf-fld textarea { resize: vertical; }
.lf-row { display: flex; flex-wrap: wrap; gap: 14px; }
.lf-row .lf-fld { flex: 1 1 220px; }
.lf-hint { margin: -2px 0 4px; color: #93a1ad; font-size: 0.78rem; }
.lf-optional { color: #93a1ad; font-weight: 500; }
.lf-options { display: flex; flex-direction: column; gap: 6px; }
.lf-opt {
  display: grid;
  grid-template-columns: 18px auto 1fr;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e6edf3;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.lf-opt:hover { border-color: #d3dde7; }
.lf-opt.on { border-color: var(--stora-accent, #4F7EB3); background: #f5f8fc; }
.lf-opt-name { color: #1d2b3a; font-size: 0.88rem; font-weight: 600; }
.lf-opt-hint { color: #93a1ad; font-size: 0.78rem; }
.lf-checks { display: flex; flex-wrap: wrap; gap: 8px 18px; }
.lf-check { display: inline-flex; align-items: center; gap: 6px; color: #1d2b3a; font-size: 0.86rem; }
.lf-actions { display: flex; align-items: center; gap: 12px; margin-top: 4px; }
/* Narrow Actions area (phone, tablet with the sidebar open): explanations go under
   each option instead of a squeezed third column. Queries the .act container. */
@container act (max-width: 560px) {
  .log-form { padding: 16px 14px; }
  .lf-opt { grid-template-columns: 18px minmax(0, 1fr); row-gap: 2px; }
  .lf-opt-hint { grid-column: 2; }
  .lf-actions { flex-wrap: wrap; }
}
.lf-submit {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: var(--stora-accent, #4F7EB3);
  border: none;
  border-radius: 999px;
  color: #fff;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
}
.lf-submit:disabled { opacity: 0.5; cursor: default; }
.lf-submit.is-locked { background: #f6f8fa; border: 1px solid #e6edf3; color: #93a1ad; cursor: not-allowed; }
.lf-cancel { background: none; border: none; color: #6b7a87; font: inherit; font-size: 0.84rem; font-weight: 600; cursor: pointer; }
.lf-cancel:hover { color: #1d2b3a; text-decoration: underline; }
</style>
